import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getIdeas } from '../../services/idea.service';
import { getReportHistory } from '../../services/history.service';
import { getEffectiveUserRole } from '../../services/auth.service';
import IdeaStatusBadge from './shared/IdeaStatusBadge';
import { ROLE_COMPONENT_IDS } from '../../services/auth.service';
import { IDEA_STATUSES, WORKFLOW_STEPS } from '../../constants/ideaWorkflow';
import RoleViewTabs from './shared/RoleViewTabs';
import './IdeaList.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'SHORTLISTED', label: 'Shortlisted' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'SENT_BACK', label: 'Sent Back' },
];

const QUEUE_OPTIONS = [
  { value: '', label: 'All Queues' },
  { value: 'OWNER_QUEUE', label: 'Owner Queue' },
  { value: 'ADMIN_QUEUE', label: 'Admin Queue' },
  { value: 'MANAGER_QUEUE', label: 'Manager Queue' },
  { value: 'DONE_QUEUE', label: 'Done Queue' },
  { value: 'LEGACY_QUEUE', label: 'Legacy Queue' },
];

const getQueueInfo = (idea) => {
  const status = idea?.status;
  const hasManagers = Array.isArray(idea?.assigned_managers) && idea.assigned_managers.length > 0;

  if (idea?.isLegacy || status === IDEA_STATUSES.LEGACY) {
    return { key: 'LEGACY_QUEUE', label: 'Legacy Queue', css: 'legacy' };
  }
  if (status === IDEA_STATUSES.DRAFT || status === IDEA_STATUSES.SENT_BACK) {
    return { key: 'OWNER_QUEUE', label: 'Owner Queue', css: 'owner' };
  }
  if (status === IDEA_STATUSES.SUBMITTED && !hasManagers) {
    return { key: 'ADMIN_QUEUE', label: 'Admin Queue', css: 'admin' };
  }
  if ([IDEA_STATUSES.UNDER_REVIEW, IDEA_STATUSES.SHORTLISTED].includes(status) || hasManagers) {
    return { key: 'MANAGER_QUEUE', label: 'Manager Queue', css: 'manager' };
  }
  if ([IDEA_STATUSES.APPROVED, IDEA_STATUSES.CLOSED].includes(status)) {
    return { key: 'DONE_QUEUE', label: 'Done Queue', css: 'done' };
  }
  return { key: 'ADMIN_QUEUE', label: 'Admin Queue', css: 'admin' };
};

function IdeaList() {
  const navigate = useNavigate();
  const [role, setRole] = useState(getEffectiveUserRole());

  const [ideas, setIdeas] = useState([]);
  const [legacyMode, setLegacyMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [queueFilter, setQueueFilter] = useState('');
  const [search, setSearch] = useState('');

  const isIdeaOwner = role === ROLE_COMPONENT_IDS.IDEA_OWNER;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        const data = await getIdeas(params);
        const nextIdeas = Array.isArray(data)
          ? data
          : (data?.ideas || data?.items || data?.data || []);

        if (Array.isArray(nextIdeas) && nextIdeas.length > 0) {
          setIdeas(nextIdeas);
          setLegacyMode(false);
        } else if (role === ROLE_COMPONENT_IDS.IDEA_OWNER && !statusFilter) {
          // Fallback: show legacy idea-analyzer reports as read-only entries.
          const history = await getReportHistory();
          const reports = history?.reports || [];
          const legacyIdeas = reports.map((r) => ({
            id: r.id,
            title: r.title || r.generated_title || r.idea || 'Legacy idea entry',
            description: r.summary || r.idea || '',
            submitted_at: r.created_at || r.timestamp,
            submitted_by: r.created_by || r.user_email || '',
            category: r.category || 'LEGACY',
            tags: Array.isArray(r.tags) ? r.tags : [],
            status: IDEA_STATUSES.LEGACY,
            isLegacy: true,
            legacyReportId: r.id,
          }));
          setIdeas(legacyIdeas);
          setLegacyMode(legacyIdeas.length > 0);
        } else {
          setIdeas([]);
          setLegacyMode(false);
        }
      } catch {
        setIdeas([]);
        setLegacyMode(false);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [statusFilter, role]);

  const visible = ideas.filter((idea) => {
    const matchSearch = !search || idea.title?.toLowerCase().includes(search.toLowerCase());
    const queue = getQueueInfo(idea);
    const matchQueue = !queueFilter || queue.key === queueFilter;
    return matchSearch && matchQueue;
  });

  return (
    <div className="idea-list">
      <RoleViewTabs onChange={setRole} />

      <div className="workflow-guide">
        <h3>Workflow Guide (Admin / Manager / Idea Owner)</h3>
        <ul>
          {WORKFLOW_STEPS.map((s) => <li key={s}>{s}</li>)}
        </ul>
      </div>

      <div className="idea-list-header">
        <h2>💡 Ideas</h2>
        <div className="idea-list-controls">
          <input
            type="text"
            placeholder="Search by title…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select value={queueFilter} onChange={e => setQueueFilter(e.target.value)}>
            {QUEUE_OPTIONS.map((q) => (
              <option key={q.value} value={q.value}>{q.label}</option>
            ))}
          </select>
          {isIdeaOwner && (
            <button className="btn-new-idea" onClick={() => navigate('/ideas/new')}>
              + New Idea
            </button>
          )}
        </div>
      </div>

      {!isIdeaOwner && (
        <div className="legacy-note" style={{ marginTop: 0 }}>
          To create an idea, switch to the Idea Owner role tab (top role switcher) and click New Idea.
        </div>
      )}

      {loading ? (
        <div className="loading-row">Loading ideas…</div>
      ) : visible.length === 0 ? (
        <div className="empty-state">
          <h3>No ideas found</h3>
          {isIdeaOwner ? (
            <>
              <p>You haven't submitted any ideas yet.</p>
              <button className="btn-new-idea" onClick={() => navigate('/ideas/new')}>
                Submit Your First Idea
              </button>
            </>
          ) : (
            <p>
              No ideas match the current filter. Managers only see ideas assigned by Admin.
              Admin can see all ideas and assign managers.
            </p>
          )}
        </div>
      ) : (
        visible.map(idea => (
          <IdeaCard
            key={idea.id || idea._id}
            idea={idea}
            onClick={() => {
              if (idea.isLegacy && idea.legacyReportId) {
                navigate(`/tools/idea-analyzer/reports/${idea.legacyReportId}`);
                return;
              }
              navigate(`/ideas/${idea.id || idea._id}`);
            }}
          />
        ))
      )}

      {legacyMode && (
        <div className="legacy-note">
          Showing legacy entries from Idea Analyzer report history because the new /ideas store has no records yet.
        </div>
      )}
    </div>
  );
}

function IdeaCard({ idea, onClick }) {
  const queue = getQueueInfo(idea);
  const submittedAt = idea.submitted_at
    ? new Date(idea.submitted_at).toLocaleDateString()
    : idea.created_at
    ? new Date(idea.created_at).toLocaleDateString()
    : null;

  return (
    <div className="idea-card" onClick={onClick}>
      <div className="idea-card-top">
        <span className="idea-card-title">{idea.title}</span>
        <div className="idea-card-badges">
          <span className={`queue-badge ${queue.css}`}>{queue.label}</span>
          <IdeaStatusBadge status={idea.status} />
        </div>
      </div>

      {idea.description && (
        <p className="idea-card-description">{idea.description}</p>
      )}

      <div className="idea-card-meta">
        {idea.category && <span>📂 {idea.category.replace('_', '/')}</span>}
        {submittedAt && <span>📅 {submittedAt}</span>}
        {idea.submitted_by && <span>👤 {idea.submitted_by}</span>}
        {idea.assigned_managers?.length > 0 && (
          <span>👥 {idea.assigned_managers.length} manager(s)</span>
        )}
      </div>

      {idea.tags?.length > 0 && (
        <div className="idea-card-tags">
          {idea.tags.map(tag => (
            <span key={tag} className="idea-card-tag">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default IdeaList;
